import type { HelmInquiryPriority, WorkflowOperationalState } from './api/types'
import {
  dueAtFromDateInput,
  dueDateInputValue,
  type QuickEditChange,
  type QuickEditRow,
  type StatusChoice,
} from './quick-edit'

/**
 * Changing many rows at once from a list, as Linear does: the same rules as
 * quick edit, applied to every selected row, and a plain account of what
 * changed and what did not.
 */

/** No more than this many records in one batch; the API holds to the same. */
export const BATCH_LIMIT = 100

export interface SharedStatus {
  status: string
  /** Every selected row can move here. */
  available: boolean
  /** The rows that cannot, when some cannot. */
  unavailableFor: string[]
}

/**
 * The statuses on offer across the selected rows, in the order given: each is
 * available only when every row can move there (or already is there).
 */
export function sharedStatusChoices<T extends { id: string }>(
  rows: readonly T[],
  choicesFor: (row: T) => StatusChoice[],
  order: readonly string[],
): SharedStatus[] {
  const offered = rows.map((row) => new Set(choicesFor(row).map((choice) => choice.status)))
  const union = new Set(offered.flatMap((statuses) => [...statuses]))
  const ordered = [
    ...order.filter((status) => union.has(status)),
    ...[...union].filter((status) => !order.includes(status)),
  ]
  return ordered.map((status) => {
    const unavailableFor = rows
      .filter((_, index) => !offered[index]!.has(status))
      .map((row) => row.id)
    return { status, available: unavailableFor.length === 0, unavailableFor }
  })
}

/** One row's part of a batch: what changes, shown on the row at once. */
export interface BatchEntry<T> {
  row: T
  change: QuickEditChange & { priority?: HelmInquiryPriority }
}

type BatchRow = QuickEditRow & {
  id: string
  workflow_phase?: string
  operational_state?: WorkflowOperationalState | string
  priority?: string
}

/** Rows moving to `status`; the ones already there are left out. */
export function statusEntries<T extends BatchRow>(
  rows: readonly T[],
  status: string,
  choicesFor: (row: T) => StatusChoice[],
): Array<BatchEntry<T>> {
  return rows.flatMap((row) => {
    if (row.status === status) return []
    const choice = choicesFor(row).find((candidate) => candidate.status === status)
    return choice
      ? [
          {
            row,
            change: {
              status: choice.status,
              workflow_phase: choice.workflow_phase,
              operational_state: choice.operational_state,
            },
          },
        ]
      : []
  })
}

/** Rows given this owner; empty means nobody. Rows that have them already are left out. */
export function ownerEntries<T extends BatchRow>(
  rows: readonly T[],
  ownerUserId: string,
): Array<BatchEntry<T>> {
  const owner = ownerUserId || null
  return rows
    .filter((row) => (row.owner_user_id ?? null) !== owner)
    .map((row) => ({ row, change: { owner_user_id: owner } }))
}

/**
 * Rows due on this day; empty clears the date. Each keeps its own time of
 * day, as quick edit does. Rows due that day already are left out.
 */
export function dueDateEntries<T extends BatchRow>(
  rows: readonly T[],
  date: string,
): Array<BatchEntry<T>> {
  return rows
    .filter((row) => dueDateInputValue(row.sla_due_at) !== date)
    .map((row) => ({ row, change: { sla_due_at: dueAtFromDateInput(date, row.sla_due_at) } }))
}

export function priorityEntries<T extends BatchRow>(
  rows: readonly T[],
  priority: HelmInquiryPriority,
): Array<BatchEntry<T>> {
  return rows
    .filter((row) => row.priority !== priority)
    .map((row) => ({ row, change: { priority } }))
}

/** "Updated 8, 2 couldn't change", or the half of it that applies. */
export function batchSummary(updated: number, failed: number) {
  const parts = [
    updated ? `Updated ${updated}` : null,
    failed ? `${failed} couldn't change` : null,
  ].filter(Boolean)
  return parts.length ? parts.join(', ') : 'Nothing needed changing'
}
